/**
 * Eventos de conversión para GA4 vía Google Tag Manager (dataLayer).
 * Nunca envía datos personales. Nunca rompe la UI.
 */
const BLOCKED_KEYS = new Set([
  "email","mail","customer_email","name","first_name","last_name","full_name","nombre","apellidos",
  "phone","telefono","address","direccion","zip_code","postal_code","message","mensaje","question",
  "pregunta","answer","respuesta","company","empresa","business_name","businessname","user_id","userid",
]);

type Params = Record<string, unknown>;

const uiLanguage = (): "es" | "en" =>
  typeof window !== "undefined" && /^\/en(\/|$)/.test(window.location.pathname) ? "en" : "es";

export function trackEvent(event: string, params: Params = {}): void {
  if (typeof window === "undefined") return;
  // Admins y dispositivos del equipo: "internal" (además GTM no se carga en ellos).
  let traffic: "internal" | "external" = "external";
  try { if (localStorage.getItem("rl-analytics-admin") === "1" || /(?:^|; )rl_team=1/.test(document.cookie)) traffic = "internal"; } catch { /* noop */ }
  try {
    const w = window as unknown as { dataLayer?: unknown[] };
    w.dataLayer = w.dataLayer || [];
    const clean: Params = { ui_language: uiLanguage(), traffic_type: traffic };
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined || v === null || v === "") continue;
      if (BLOCKED_KEYS.has(k.toLowerCase())) continue;
      if (typeof v === "string" && v.includes("@")) continue;
      clean[k] = v;
    }
    if ("ecommerce" in clean) w.dataLayer.push({ ecommerce: null });
    w.dataLayer.push({ event, ...clean });
  } catch { /* la analítica nunca rompe la UI */ }
}

/** Igual que trackEvent pero solo una vez por clave (persistida en localStorage). La clave NO se envía. */
export function trackEventOnce(dedupeKey: string, event: string, params: Params = {}): void {
  try {
    const k = `rl_ga_${dedupeKey}`;
    if (localStorage.getItem(k)) return;
    localStorage.setItem(k, "1");
  } catch { /* sin almacenamiento: enviamos igualmente */ }
  trackEvent(event, params);
}

export function trackBeginCheckout(a: {
  source: string;
  productType: string;
  productKey?: string | null;
  interval?: string | null;
  valueCents?: number | null;
  itemName?: string | null;
}): void {
  try {
    const value = typeof a.valueCents === "number" ? a.valueCents / 100 : undefined;
    const item: Params = {
      item_id: a.productKey || a.productType,
      item_name: a.itemName || a.productKey || a.productType,
      item_category: a.productType,
      item_variant: a.interval || "one_time",
      quantity: 1,
    };
    if (value !== undefined) item['price'] = value;
    void import("@/lib/publicActivity").then((m) =>
      m.trackPublicEvent({ eventType: "checkout_started", toolKey: null, metadata: { checkout_source: a.source, product_type: a.productType } }),
    ).catch(() => {});
    trackEvent("begin_checkout", {
      checkout_source: a.source,
      product_type: a.productType,
      billing_interval: a.interval || "one_time",
      ecommerce: { currency: "EUR", ...(value !== undefined ? { value } : {}), items: [item] },
    });
  } catch { /* noop */ }
}

/** sign_up en GA4 + signup_completed en el embudo propio (una vez). */
export function trackSignUp(userId: string, method: string): void {
  trackEventOnce(`signup_${userId}`, "sign_up", { method });
  try {
    void import("@/lib/publicActivity").then((m) =>
      m.trackPublicEvent({ eventType: "signup_completed", metadata: { method } }, { once: true }),
    ).catch(() => {});
  } catch { /* noop */ }
}

/** Marca de dispositivo del equipo (la misma que usan GTM y /api/public/track). */
export const isTeamDevice = (): boolean => {
  try { return localStorage.getItem("rl-analytics-admin") === "1" || /(?:^|; )rl_team=1/.test(document.cookie); } catch { return false; }
};
